'use client';

import { Children, type ReactNode, type UIEvent, useRef, useState } from 'react';
import Image from 'next/image';

interface SeoContentCarouselLabels {
  next: string;
  previous: string;
  slide: string;
}

interface SeoContentCarouselProps {
  children: ReactNode;
  labels: SeoContentCarouselLabels;
}

const CAROUSEL_CHEVRON_PATH = '/zeelool-next/goods-list/seo-footer/carousel-chevron.svg';

export default function SeoContentCarousel({ children, labels }: SeoContentCarouselProps) {
  const slides = Children.toArray(children);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = slides.length - 1;

  const scrollToSlide = (index: number) => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const nextIndex = Math.min(Math.max(index, 0), lastIndex);
    viewport.scrollTo({
      left: nextIndex * viewport.clientWidth,
    });
  };

  const handleScroll = (event: UIEvent<HTMLDivElement>) => {
    const viewport = event.currentTarget;
    if (!viewport.clientWidth) return;

    const nextIndex = Math.min(
      Math.max(Math.round(viewport.scrollLeft / viewport.clientWidth), 0),
      lastIndex,
    );
    setActiveIndex(nextIndex);
  };

  if (slides.length === 0) return null;

  return (
    <div className="flex w-full flex-col gap-[19px] min-[920px]:gap-[24px]">
      <div
        data-seo-carousel-card
        className="relative min-h-[240px] overflow-hidden rounded-[8px] bg-white min-[920px]:min-h-[203px]"
      >
        <div
          ref={viewportRef}
          data-seo-carousel-viewport
          className="scrollbar-hide flex min-h-[240px] snap-x snap-mandatory items-stretch overflow-x-auto overscroll-x-contain scroll-smooth motion-reduce:scroll-auto [-webkit-overflow-scrolling:touch] min-[920px]:min-h-[203px]"
          onScroll={handleScroll}
        >
          {slides.map((slide, index) => (
            <div key={index} className="flex w-full min-w-0 shrink-0 snap-start">
              {slide}
            </div>
          ))}
        </div>

        {slides.length > 1 && activeIndex > 0 ? (
          <button
            type="button"
            aria-label={labels.previous}
            className="absolute left-[28px] top-1/2 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
            onClick={() => scrollToSlide(activeIndex - 1)}
          >
            <Image
              src={CAROUSEL_CHEVRON_PATH}
              alt=""
              width={15}
              height={15}
              className="rotate-90"
            />
          </button>
        ) : null}

        {slides.length > 1 && activeIndex < lastIndex ? (
          <button
            type="button"
            aria-label={labels.next}
            className="absolute right-[28px] top-1/2 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
            onClick={() => scrollToSlide(activeIndex + 1)}
          >
            <Image
              src={CAROUSEL_CHEVRON_PATH}
              alt=""
              width={15}
              height={15}
              className="-rotate-90"
            />
          </button>
        ) : null}
      </div>

      {slides.length > 1 ? (
        <div
          data-seo-carousel-pagination
          className="flex h-[3px] items-center justify-center gap-[8px]"
        >
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-current={activeIndex === index ? 'true' : undefined}
              aria-label={labels.slide.replace('{index}', String(index + 1))}
              className="flex h-[24px] items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
              onClick={() => scrollToSlide(index)}
            >
              <span
                className={`h-[3px] rounded-[99px] transition-[width,background-color] motion-reduce:transition-none ${
                  activeIndex === index
                    ? 'w-[40px] bg-primary-purple'
                    : 'w-[24px] bg-primary-purple-disable'
                }`}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
